/**
 * Trazer ações do catálogo para o plano (issue #359, D11).
 *
 * O catálogo é semente, não conteúdo do plano: quem escolhe o que entra é a pessoa. Uma empresa
 * de escritório que recebesse as 77 ações começaria apagando caldeira, espaço confinado e
 * trabalho em altura, linha por linha.
 *
 * Cada ação trazida vira uma cópia editável, desligada do catálogo a partir daí.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { actionPlanService } from '../../../services/actionPlanService';
import type { ActionPlanCatalogNorma } from '../../../types/actionPlan';

export interface CatalogImportDialogProps {
  aberto: boolean;
  trazendo?: boolean;
  onFechar: () => void;
  /** `null` traz o catálogo inteiro; a lista traz só as normas marcadas. */
  onTrazer: (normas: string[] | null) => void;
}

export const CatalogImportDialog: React.FC<CatalogImportDialogProps> = ({
  aberto,
  trazendo = false,
  onFechar,
  onTrazer,
}) => {
  const [catalogo, setCatalogo] = useState<ActionPlanCatalogNorma[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [marcadas, setMarcadas] = useState<string[]>([]);

  useEffect(() => {
    if (!aberto) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    void actionPlanService
      .listarCatalogo()
      .then((normas) => {
        if (!ativo) return;
        setCatalogo(normas);
        setMarcadas(normas.map((linha) => linha.norma));
      })
      .catch((err: unknown) => {
        if (ativo) setErro(err instanceof Error ? err.message : 'Falha ao ler o catálogo');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [aberto]);

  const todasMarcadas = catalogo.length > 0 && marcadas.length === catalogo.length;

  const totalDeAcoes = useMemo(
    () =>
      catalogo
        .filter((linha) => marcadas.includes(linha.norma))
        .reduce((soma, linha) => soma + linha.quantidade, 0),
    [catalogo, marcadas]
  );

  const alternar = useCallback((norma: string) => {
    setMarcadas((atuais) =>
      atuais.includes(norma) ? atuais.filter((uma) => uma !== norma) : [...atuais, norma]
    );
  }, []);

  const alternarTodas = useCallback(() => {
    setMarcadas((atuais) =>
      atuais.length === catalogo.length ? [] : catalogo.map((linha) => linha.norma)
    );
  }, [catalogo]);

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Trazer ações do catálogo</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada ação entra como cópia deste plano, com prioridade, responsável e prazo já
          preenchidos — e você edita o que quiser depois.
        </Typography>

        {erro && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {carregando ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={todasMarcadas}
                  indeterminate={marcadas.length > 0 && !todasMarcadas}
                  onChange={alternarTodas}
                />
              }
              label={`Todas as normas (${catalogo.length})`}
            />
            <Divider sx={{ my: 1 }} />
            <Stack sx={{ maxHeight: 340, overflowY: 'auto' }}>
              {catalogo.map((linha) => (
                <FormControlLabel
                  key={linha.norma}
                  control={
                    <Checkbox
                      checked={marcadas.includes(linha.norma)}
                      onChange={() => alternar(linha.norma)}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{linha.norma}</span>
                      <Chip size="small" variant="outlined" label={`${linha.quantidade} ações`} />
                    </Stack>
                  }
                />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={trazendo || marcadas.length === 0}
          onClick={() => onTrazer(todasMarcadas ? null : marcadas)}
        >
          {trazendo ? 'Trazendo…' : `Trazer ${totalDeAcoes} ações`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogImportDialog;
